import React from 'react';
import { Badge } from '@/components/ui/badge';
import type { ServiceEngagement } from '@/types';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  expired: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  revoked: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

const SPECIALIZATION_LABELS: Record<string, string> = {
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  hvac: 'HVAC',
  security: 'Security',
  networking: 'Networking',
  solar: 'Solar',
  av: 'A/V',
  general: 'General',
};

interface EngagementTimelineProps {
  engagements: ServiceEngagement[];
}

const EngagementTimeline: React.FC<EngagementTimelineProps> = ({ engagements }) => {
  if (engagements.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">No engagements yet.</p>
    );
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-3">
      {engagements.map((engagement) => {
        const scope = engagement.specializationScope;
        return (
          <div
            key={engagement._id}
            className="flex items-center gap-4 p-3 rounded-lg border border-border"
          >
            {/* Status dot */}
            <div
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                engagement.status === 'active' ? 'bg-green-500' :
                engagement.status === 'pending' ? 'bg-yellow-500' :
                engagement.status === 'completed' ? 'bg-blue-500' :
                engagement.status === 'expired' ? 'bg-gray-400' :
                'bg-red-500'
              }`}
            />

            {/* Specialization and details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm">
                  {SPECIALIZATION_LABELS[scope] || scope}
                </span>
                <Badge className={STATUS_COLORS[engagement.status] || ''}>
                  {engagement.status}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {formatDate(engagement.accessWindow.startDate)} &mdash; {formatDate(engagement.accessWindow.endDate)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EngagementTimeline;
